import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PublicNav, PublicFooter } from "@/components/PublicShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Target, Eye, Rocket, ShieldCheck, CheckCircle2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { CATEGORIES, EMPLOYMENT_TYPES, ARRANGEMENTS, SENIORITY, emptyJobFields } from "@/lib/jobFields";

const BENEFITS = [
  { icon: Target, title: "Engaged executive search", body: "Deliberate, recruiter-led searches across executive, platform, operational, venture, board and fractional roles." },
  { icon: Eye, title: "Full pipeline visibility", body: "See your active searches, recruiter-shared shortlists and interview status in a private client portal." },
  { icon: Rocket, title: "Post roles to vetted talent", body: "Publish standard or featured job postings directly to our candidate network with alerts." },
];

const inputCls = "bg-[#F4F4F5] border-black/10 rounded-none";

export default function ForClients() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("lead");
  const [packages, setPackages] = useState([]);
  useEffect(() => {
    api.get("/billing/config").then(({ data }) => setPackages(data.job_posting_packages)).catch(() => {});
  }, []);

  // lead form
  const [saving, setSaving] = useState(false);
  const [sent, setSent] = useState(false);
  const [lead, setLead] = useState({
    company: "", contact_name: "", email: "", phone: "", roles: [{ title: "", seniority: "" }],
    timeline: "flexible", engagement_type: "engaged", budget: "", message: "",
  });
  const setL = (k) => (e) => setLead({ ...lead, [k]: e.target.value });
  const setRole = (i, key, v) => { const arr = lead.roles.map((r, j) => (j === i ? { ...r, [key]: v } : r)); setLead({ ...lead, roles: arr }); };
  const addRole = () => setLead({ ...lead, roles: [...lead.roles, { title: "", seniority: "" }] });
  const removeRole = (i) => setLead({ ...lead, roles: lead.roles.filter((_, j) => j !== i) });

  const submitLead = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/leads", { ...lead, roles: lead.roles.map((r) => ({ title: r.title.trim(), seniority: r.seniority })).filter((r) => r.title) });
      setSent(true);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  // self-serve signup + post a job
  const [sf, setSf] = useState({
    full_name: "", email: "", password: "", company_name: "", website: "", type: "startup", industry_tags: "",
    job_title: "", description: "", package_id: "standard", ...emptyJobFields,
  });
  const [jobPdf, setJobPdf] = useState(null);
  const setS = (k) => (e) => setSf({ ...sf, [k]: e.target.value });
  const submitSignup = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await register({ full_name: sf.full_name, email: sf.email, password: sf.password, role: "client" });
      await api.patch("/client/organization", {
        name: sf.company_name || `${sf.full_name}'s Organization`, type: sf.type, website: sf.website,
        industry_tags: sf.industry_tags.split(",").map((x) => x.trim()).filter(Boolean),
      });
      const { data: job } = await api.post("/jobs", {
        title: sf.job_title, description: sf.description, role_category: sf.role_category,
        employment_type: sf.employment_type, work_arrangement: sf.work_arrangement, seniority: sf.seniority,
        location: sf.location, compensation_range: sf.compensation_range,
        responsibilities: sf.responsibilities, requirements: sf.requirements,
        application_deadline: sf.application_deadline, apply_method: sf.apply_method, apply_link: sf.apply_link,
      });
      if (jobPdf) {
        const fd = new FormData();
        fd.append("file", jobPdf);
        await api.post(`/jobs/${job.id}/attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      const { data: co } = await api.post("/billing/checkout", {
        kind: "job_posting", package_id: sf.package_id, job_id: job.id, origin_url: window.location.origin,
      });
      window.location.href = co.url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#F9F9FA] text-[#09090B]">
      <PublicNav />
      <section className="relative pt-16">
        <div className="max-w-[1400px] mx-auto px-6 py-16 lg:py-24 grid lg:grid-cols-12 gap-12 items-start">
          {/* Left */}
          <div className="lg:col-span-5">
            <p className="overline mb-4">For Clients</p>
            <h1 className="font-heading font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              Find the few<br />who move<br /><span className="text-primary">everything forward.</span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-lg">
              We partner with venture funds, accelerators, studios, universities, corporate innovation teams
              and venture-backed startups. Start an engaged search with our team, or post a role yourself.
            </p>
            <div className="mt-10 space-y-px bg-black/10 border border-black/10">
              {BENEFITS.map((b, i) => (
                <motion.div key={b.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                  className="bg-white p-6 flex gap-4">
                  <b.icon className="h-6 w-6 text-primary shrink-0" strokeWidth={1.5} />
                  <div><h3 className="font-heading font-bold">{b.title}</h3><p className="text-sm text-muted-foreground mt-1">{b.body}</p></div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Right: tabbed panel */}
          <div className="lg:col-span-7 lg:sticky lg:top-24">
            <div className="border border-black/10 bg-white">
              <div className="grid grid-cols-2 border-b border-black/10">
                <button data-testid="tab-lead" onClick={() => { setTab("lead"); }} className={`p-5 text-left transition-colors ${tab === "lead" ? "bg-[#09090B] text-white" : "bg-white hover:bg-black/5"}`}>
                  <p className="font-heading font-bold">Request a search</p>
                  <p className={`text-xs mt-1 ${tab === "lead" ? "text-white/60" : "text-muted-foreground"}`}>Engaged · recruiter-led</p>
                </button>
                <button data-testid="tab-signup" onClick={() => { setTab("signup"); }} className={`p-5 text-left transition-colors border-l border-black/10 ${tab === "signup" ? "bg-[#09090B] text-white" : "bg-white hover:bg-black/5"}`}>
                  <p className="font-heading font-bold">Post a job now</p>
                  <p className={`text-xs mt-1 ${tab === "signup" ? "text-white/60" : "text-muted-foreground"}`}>Self-serve · instant</p>
                </button>
              </div>

              {tab === "lead" ? (
                sent ? (
                  <div className="p-10 text-center" data-testid="lead-thankyou">
                    <CheckCircle2 className="h-12 w-12 text-[#10B981] mx-auto" strokeWidth={1.5} />
                    <h2 className="font-heading font-bold text-2xl mt-6">Thank you — we'll be in touch</h2>
                    <p className="text-sm text-muted-foreground mt-2 max-w-sm mx-auto">A partner will review your search and reach out within one business day to scope the engagement. Keep an eye on your inbox.</p>
                  </div>
                ) : (
                  <form onSubmit={submitLead} className="p-8 space-y-4" data-testid="lead-form">
                    <p className="text-sm text-muted-foreground">Tell us about the search. No account needed — a partner will follow up and set up your workspace.</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2"><Label>Company</Label><Input data-testid="lead-company" value={lead.company} onChange={setL("company")} required className={inputCls} /></div>
                      <div className="space-y-2"><Label>Your name</Label><Input data-testid="lead-name" value={lead.contact_name} onChange={setL("contact_name")} required className={inputCls} /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2"><Label>Work email</Label><Input type="email" data-testid="lead-email" value={lead.email} onChange={setL("email")} required className={inputCls} /></div>
                      <div className="space-y-2"><Label>Phone</Label><Input data-testid="lead-phone" value={lead.phone} onChange={setL("phone")} className={inputCls} /></div>
                    </div>
                    <div className="space-y-2">
                      <Label>Role(s) hiring for <span className="text-muted-foreground font-normal">— set seniority per role</span></Label>
                      <div className="space-y-2">
                        {lead.roles.map((r, i) => (
                          <div key={i} className="flex gap-2">
                            <Input data-testid={`lead-role-${i}`} value={r.title} onChange={(e) => setRole(i, "title", e.target.value)} placeholder="e.g. VP Engineering" className={`${inputCls} flex-1`} />
                            <Select value={r.seniority} onValueChange={(v) => setRole(i, "seniority", v)}>
                              <SelectTrigger data-testid={`lead-role-seniority-${i}`} className={`${inputCls} w-[46%]`}><SelectValue placeholder="Seniority" /></SelectTrigger>
                              <SelectContent className="bg-white border-black/10 max-h-64">
                                {SENIORITY.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            {lead.roles.length > 1 && (
                              <button type="button" data-testid={`lead-remove-role-${i}`} onClick={() => removeRole(i)} className="shrink-0 w-11 flex items-center justify-center bg-[#F4F4F5] border border-black/10 hover:bg-black/5 transition-colors">
                                <X className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        ))}
                        <button type="button" data-testid="lead-add-role" onClick={addRole} className="text-xs font-medium text-primary hover:underline flex items-center gap-1">
                          <Plus className="h-3.5 w-3.5" /> Add another role
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-2">
                        <Label>Timeline</Label>
                        <Select value={lead.timeline} onValueChange={(v) => setLead({ ...lead, timeline: v })}>
                          <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white border-black/10">
                            {["urgent", "1_3_months", "flexible"].map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Engagement</Label>
                        <Select value={lead.engagement_type} onValueChange={(v) => setLead({ ...lead, engagement_type: v })}>
                          <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white border-black/10">
                            {["engaged", "contingent", "fractional", "advisory"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2"><Label>Budget</Label><Input data-testid="lead-budget" value={lead.budget} onChange={setL("budget")} placeholder="$50k fee" className={inputCls} /></div>
                    </div>
                    <div className="space-y-2"><Label>Tell us about the search</Label><Textarea data-testid="lead-message" value={lead.message} onChange={setL("message")} className={`${inputCls} min-h-[90px]`} placeholder="Context, must-haves, company stage…" /></div>
                    <Button type="submit" disabled={saving} data-testid="lead-submit" className="w-full h-11 rounded-none bg-primary hover:bg-[#09090B] transition-colors">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Request a search"}
                    </Button>
                  </form>
                )
              ) : (
                <form onSubmit={submitSignup} className="p-8 space-y-5" data-testid="client-signup-form">
                  <p className="text-sm text-muted-foreground">Post your role in one step. Create your account, add the job, choose a package and pay — your listing goes <b className="text-[#09090B]">live instantly</b> after payment.</p>

                  {/* 1. Package */}
                  <div className="space-y-2">
                    <p className="overline text-[0.55rem]">1 · Choose a package</p>
                    <div className="grid grid-cols-2 gap-px bg-black/10 border border-black/10">
                      {packages.map((p) => {
                        const on = sf.package_id === p.id;
                        return (
                          <button type="button" key={p.id} data-testid={`pkg-${p.id}`} onClick={() => setSf({ ...sf, package_id: p.id })}
                            className={`p-4 text-left transition-colors ${on ? "bg-[#09090B] text-white" : "bg-[#F4F4F5] hover:bg-black/5"}`}>
                            <div className="flex items-baseline justify-between">
                              <p className="font-heading font-bold text-sm">{p.name.replace(" Posting", "")}</p>
                              <p className="font-heading font-black text-xl">${p.price}</p>
                            </div>
                            <p className={`text-[0.6rem] mt-1 ${on ? "text-white/60" : "text-muted-foreground"}`}>{p.features[0]}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* 2. Job details */}
                  <div className="space-y-3">
                    <p className="overline text-[0.55rem]">2 · Your role</p>
                    <div className="space-y-2"><Label>Job title</Label><Input data-testid="job-title" value={sf.job_title} onChange={setS("job_title")} required placeholder="e.g. VP of Engineering" className={inputCls} /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label>Category</Label>
                        <Select value={sf.role_category} onValueChange={(v) => setSf({ ...sf, role_category: v })}>
                          <SelectTrigger data-testid="job-category" className={`${inputCls} capitalize`}><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white border-black/10">
                            {CATEGORIES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Seniority</Label>
                        <Select value={sf.seniority} onValueChange={(v) => setSf({ ...sf, seniority: v })}>
                          <SelectTrigger data-testid="job-seniority" className={inputCls}><SelectValue placeholder="Select level" /></SelectTrigger>
                          <SelectContent className="bg-white border-black/10 max-h-64">
                            {SENIORITY.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label>Employment type</Label>
                        <Select value={sf.employment_type} onValueChange={(v) => setSf({ ...sf, employment_type: v })}>
                          <SelectTrigger data-testid="job-employment" className={inputCls}><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white border-black/10">
                            {EMPLOYMENT_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Work arrangement</Label>
                        <Select value={sf.work_arrangement} onValueChange={(v) => setSf({ ...sf, work_arrangement: v })}>
                          <SelectTrigger data-testid="job-arrangement" className={inputCls}><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white border-black/10">
                            {ARRANGEMENTS.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2"><Label>Location</Label><Input data-testid="job-location" value={sf.location} onChange={setS("location")} placeholder="Remote · SF · NYC" className={inputCls} /></div>
                      <div className="space-y-2"><Label>Compensation</Label><Input data-testid="job-comp" value={sf.compensation_range} onChange={setS("compensation_range")} placeholder="$200k–$260k + equity" className={inputCls} /></div>
                    </div>
                    <div className="space-y-2"><Label>Overview / description</Label><Textarea data-testid="job-description" value={sf.description} onChange={setS("description")} required className={`${inputCls} min-h-[80px]`} placeholder="Company context, the opportunity, why this role matters…" /></div>
                    <div className="space-y-2"><Label>Key responsibilities</Label><Textarea data-testid="job-responsibilities" value={sf.responsibilities} onChange={setS("responsibilities")} className={`${inputCls} min-h-[70px]`} placeholder="What this person will own and drive…" /></div>
                    <div className="space-y-2"><Label>Requirements / must-haves</Label><Textarea data-testid="job-requirements" value={sf.requirements} onChange={setS("requirements")} className={`${inputCls} min-h-[70px]`} placeholder="Experience, skills and background required…" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2"><Label>Application deadline <span className="text-muted-foreground font-normal">(optional)</span></Label><Input type="date" data-testid="job-deadline" value={sf.application_deadline} onChange={setS("application_deadline")} className={inputCls} /></div>
                      <div className="space-y-2">
                        <Label>How to apply</Label>
                        <Select value={sf.apply_method} onValueChange={(v) => setSf({ ...sf, apply_method: v })}>
                          <SelectTrigger data-testid="job-apply-method" className={inputCls}><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white border-black/10">
                            <SelectItem value="portal">Signal interest via portal</SelectItem>
                            <SelectItem value="external">External link / email</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {sf.apply_method === "external" && (
                      <div className="space-y-2"><Label>Application link or email</Label><Input data-testid="job-apply-link" value={sf.apply_link} onChange={setS("apply_link")} placeholder="https://… or jobs@company.com" className={inputCls} /></div>
                    )}
                    <div className="space-y-2">
                      <Label>Attach job description <span className="text-muted-foreground font-normal">(optional PDF)</span></Label>
                      <input type="file" accept=".pdf" data-testid="job-pdf"
                        onChange={(e) => setJobPdf(e.target.files?.[0] || null)}
                        className="w-full text-sm file:mr-3 file:py-2 file:px-4 file:border file:border-black/10 file:bg-[#F4F4F5] file:text-sm file:cursor-pointer" />
                      {jobPdf && <p className="text-xs text-muted-foreground">Selected: {jobPdf.name}</p>}
                    </div>
                  </div>

                  {/* 3. Account */}
                  <div className="space-y-3">
                    <p className="overline text-[0.55rem]">3 · Your account</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2"><Label>Your name</Label><Input data-testid="client-signup-name" value={sf.full_name} onChange={setS("full_name")} required className={inputCls} /></div>
                      <div className="space-y-2"><Label>Work email</Label><Input type="email" data-testid="client-signup-email" value={sf.email} onChange={setS("email")} required className={inputCls} /></div>
                    </div>
                    <div className="space-y-2"><Label>Password</Label><Input type="password" data-testid="client-signup-password" value={sf.password} onChange={setS("password")} required className={inputCls} /></div>
                    <div className="space-y-2"><Label>Company / organization</Label><Input data-testid="client-signup-company" value={sf.company_name} onChange={setS("company_name")} required className={inputCls} /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label>Organization type</Label>
                        <Select value={sf.type} onValueChange={(v) => setSf({ ...sf, type: v })}>
                          <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white border-black/10">
                            <SelectItem value="startup">Venture-backed startup</SelectItem>
                            <SelectItem value="venture_fund">Venture capital fund</SelectItem>
                            <SelectItem value="scaleup">Scaleup</SelectItem>
                            <SelectItem value="enterprise">Corporate innovation</SelectItem>
                            <SelectItem value="portfolio_company">Studio / accelerator</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2"><Label>Website</Label><Input value={sf.website} onChange={setS("website")} placeholder="https://" className={inputCls} /></div>
                    </div>
                    <div className="space-y-2"><Label>Industry tags</Label><Input value={sf.industry_tags} onChange={setS("industry_tags")} placeholder="fintech, ai, climate" className={inputCls} /></div>
                  </div>

                  <Button type="submit" disabled={saving} data-testid="client-signup-submit" className="w-full h-11 rounded-none bg-primary hover:bg-[#09090B] transition-colors">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : `Continue to secure checkout · $${packages.find((p) => p.id === sf.package_id)?.price ?? ""}`}
                  </Button>
                  <p className="text-xs text-muted-foreground text-center flex items-center justify-center gap-1"><ShieldCheck className="h-3 w-3" /> Secure Stripe checkout · listing goes live on payment.</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
      <PublicFooter />
    </div>
  );
}
